import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { getLocalStorageItem, removeLocalStorageItem, LOCAL_STORAGE_KEYS } from "../utils/localStorage";
import { AUTH_STACK } from "../utils/screenNames";

import OverlayLoader from "./Loader/OverlayLoader";

/**
 * ProtectedRoute - JSX component for route authentication
 * Wrapper component that protects routes requiring authentication
 * 
 * Features:
 * - Checks for valid access token in localStorage
 * - Redirects to login if unauthenticated
 * - Shows loading state during auth check
 * - Preserves attempted URL for post-login redirect
 * 
 * @param {React.ReactNode} children - Protected content to render
 */
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = () => {
      try {
        const accessToken = getLocalStorageItem(LOCAL_STORAGE_KEYS.ACCESS);
        setIsAuthenticated(!!accessToken);
      } catch (error) {
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [location]);

  if (loading) {
    return <OverlayLoader />;
  }

  if (!isAuthenticated) {
    // Redirect to login while saving the attempted URL
    return (
      <Navigate
        to={AUTH_STACK.SIGNUP_OPTIONS}
        state={{ from: location }}
        replace
      />
    );
  }

  // Check for pending member invitation redirect (after login/signup)
  const pending = getLocalStorageItem(LOCAL_STORAGE_KEYS.PENDING_MEMBER_INVITATION);
  if (pending?.token) {
    removeLocalStorageItem(LOCAL_STORAGE_KEYS.PENDING_MEMBER_INVITATION);
    return <Navigate to={`/member-invitation/${pending.token}`} replace />;
  }

  return children;
};

export default ProtectedRoute;
